/* eslint-disable*/
import React, { useEffect, useState, useRef, useCallback } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHome,
  faArrowUp,
  faArrowDown,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import moment from "moment";
import { Chart, registerables } from "chart.js";
import { addMonths, subMonths, startOfMonth } from "date-fns";

// Register Chart.js components
Chart.register(...registerables);

const LeadSourceReport = () => {
  // New: granular loading states for different sections
  const [loadingStates, setLoadingStates] = useState({
    summary: false,
    chart: false,
    table: false
  });

  const chartRef = useRef(null);
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));
  const chartInstanceRef = useRef(null);

  // --- Date Logic: Calculate Current Week (Mon-Sun) ---
  const defaultStart = moment().startOf("isoWeek");
  const defaultEnd = moment().endOf("isoWeek");
  const defaultLabel = `${defaultStart.format("DD-MM-YYYY")} - ${defaultEnd.format("DD-MM-YYYY")}`;

  // Form state - Initialized with Current Week
  const [formInputs, setFormInputs] = useState({
    created_on: defaultLabel,
    screated_on: defaultStart.toDate(),
    ecreated_on: defaultEnd.toDate(),
    selecteduser: [],
  });

  // Filter state to trigger API calls - Initialized with Current Week
  const [filters, setFilters] = useState({
    created_on: defaultLabel,
    screated_on: defaultStart.format("DD-MM-YYYY"),
    ecreated_on: defaultEnd.format("DD-MM-YYYY"),
    selecteduser: [],
  });

  // Report data state
  const [reportData, setReportData] = useState({
    leadSourceCountData: {},
    leadSourceReportData: [],
  });

  // Summary boxes state
  const [summaryBoxes, setSummaryBoxes] = useState({
    totalRevenue: { current: 0, previous: 0, difference: 0, html: "" },
    totalCost: { current: 0, previous: 0, difference: 0, html: "" },
    totalLeadConRatio: { current: 0, previous: 0, difference: 0, html: "" },
    totalLeads: { current: 0, previous: 0, difference: 0, html: "" },
    totalSubscribedLeads: { current: 0, previous: 0, difference: 0, html: "" },
  });

  // Define Ranges for the Bootstrap Daterangepicker
  const ranges = {
    "This Week": [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
    "This Year": [moment().startOf("year"), moment().endOf("year")],
    "Last Year": [
      moment().subtract(1, "year").startOf("year"),
      moment().subtract(1, "year").endOf("year"),
    ],
  };

  // Function to update loading state for a specific key
  const updateLoadingState = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  // Fetch lead source data
  const fetchLeadSourceData = useCallback(async () => {
    try {
      // Set all sections to loading initially
      setLoadingStates({
        summary: true,
        chart: true,
        table: true
      });

      const token = localStorage.getItem("authToken");

      // Prepare request payload based on API structure
      const requestPayload = {
        created_on: filters.created_on,
        screated_on: filters.screated_on,
        ecreated_on: filters.ecreated_on,
        selecteduser: filters.selecteduser,
      };

      const response = await axios.post(
        `${API_URL}report-lead-source`,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        setReportData({
          leadSourceCountData: response.data.leadSourceCountData || {},
          leadSourceReportData: response.data.leadSourceReportData || [],
        });

        // Calculate summary boxes
        calculateSummaryBoxes(response.data.leadSourceCountData);

        // Update loading states with slight delays for better UX
        updateLoadingState('summary', false);
        setTimeout(() => {
          updateLoadingState('chart', false);
          updateLoadingState('table', false);
        }, 300);
      }
    } catch (error) {
      console.error("Error fetching lead source data:", error);
      setReportData({
        leadSourceCountData: {},
        leadSourceReportData: [],
      });

      // Update all loading states to false on error
      setLoadingStates({
        summary: false,
        chart: false,
        table: false
      });
    }
  }, [filters]);

  // Calculate summary boxes with percentage differences
  const calculateSummaryBoxes = (countData) => {
    if (!countData) return;

    const {
      total_revenue = 0,
      total_cost = 0,
      total_leads = 0,
      total_subscribed_leads = 0,
      pre_total_revenue = 0,
      pre_total_cost = 0,
      pre_total_leads = 0,
      pre_total_subscribed_leads = 0,
      revenue_growth = 0,
      cost_growth = 0,
      leads_growth = 0,
      subscribed_growth = 0,
    } = countData;

    // Calculate conversion ratios
    const totalLeadConRatio =
      total_subscribed_leads !== 0 && total_leads !== 0
        ? (total_subscribed_leads / total_leads) * 100
        : 0;

    const preTotalLeadConRatio =
      pre_total_subscribed_leads !== 0 && pre_total_leads !== 0
        ? (pre_total_subscribed_leads / pre_total_leads) * 100
        : 0;

    // Use growth percentages from API if available, otherwise calculate
    const revenueDiff =
      revenue_growth !== undefined
        ? revenue_growth
        : pre_total_revenue !== 0
          ? ((total_revenue - pre_total_revenue) / pre_total_revenue) * 100
          : 0;

    const costDiff =
      cost_growth !== undefined
        ? cost_growth
        : pre_total_cost !== 0
          ? ((total_cost - pre_total_cost) / pre_total_cost) * 100
          : 0;

    const leadConRatioDiff =
      preTotalLeadConRatio !== 0
        ? ((totalLeadConRatio - preTotalLeadConRatio) / preTotalLeadConRatio) *
        100
        : 0;

    const leadsDiff =
      leads_growth !== undefined
        ? leads_growth
        : pre_total_leads !== 0
          ? ((total_leads - pre_total_leads) / pre_total_leads) * 100
          : 0;

    const subscribedLeadsDiff =
      subscribed_growth !== undefined
        ? subscribed_growth
        : pre_total_subscribed_leads !== 0
          ? ((total_subscribed_leads - pre_total_subscribed_leads) /
            pre_total_subscribed_leads) *
          100
          : 0;

    setSummaryBoxes({
      totalRevenue: {
        current: total_revenue,
        previous: pre_total_revenue,
        difference: revenueDiff,
        html: generateDifferenceHtml(pre_total_revenue, revenueDiff),
      },
      totalCost: {
        current: total_cost,
        previous: pre_total_cost,
        difference: costDiff,
        html: generateDifferenceHtml(pre_total_cost, costDiff),
      },
      totalLeadConRatio: {
        current: totalLeadConRatio,
        previous: preTotalLeadConRatio,
        difference: leadConRatioDiff,
        html: generateDifferenceHtml(
          preTotalLeadConRatio,
          leadConRatioDiff,
          true
        ),
      },
      totalLeads: {
        current: total_leads,
        previous: pre_total_leads,
        difference: leadsDiff,
        html: generateDifferenceHtml(pre_total_leads, leadsDiff, false, true),
      },
      totalSubscribedLeads: {
        current: total_subscribed_leads,
        previous: pre_total_subscribed_leads,
        difference: subscribedLeadsDiff,
        html: generateDifferenceHtml(
          pre_total_subscribed_leads,
          subscribedLeadsDiff,
          false,
          true
        ),
      },
    });
  };

  // Generate HTML for difference badges
  const generateDifferenceHtml = (
    previousValue,
    difference,
    isPercentage = false,
    isCount = false
  ) => {
    const formattedValue = isPercentage
      ? Number(previousValue).toFixed(2)
      : isCount
        ? moneyFormatIndia(previousValue, true)
        : moneyFormatIndia(previousValue);

    const formattedDiff = Math.abs(Number(difference)).toFixed(2);
    const arrowIcon = difference < 0 ? faArrowDown : faArrowUp;
    const badgeClass = difference < 0 ? "badge-danger" : "badge-success";
    const sign = difference < 0 ? "" : "+";

    return (
      <>
        {formattedValue}{" "}
        <span className={`badge ${badgeClass} font-weight-normal`}>
          <FontAwesomeIcon icon={arrowIcon} /> ({sign}
          {formattedDiff}%)
        </span>
      </>
    );
  };

  useEffect(() => {
    fetchLeadSourceData();
  }, [fetchLeadSourceData]);

  // Initialize chart when report data changes
  useEffect(() => {
    if (reportData.leadSourceReportData.length >= 0 && chartRef.current) {
      renderChart();
    }

    return () => {
      // Cleanup chart instance
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [reportData.leadSourceReportData]);

  const renderChart = () => {
    // Destroy previous chart if exists
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    if (!chartRef.current || reportData.leadSourceReportData.length === 0) return;

    const ctx = chartRef.current.getContext("2d");
    chartInstanceRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: reportData.leadSourceReportData.map((item) => item.lead_source),
        datasets: [
          {
            backgroundColor: "#127BBC",
            borderColor: "#127BBC",
            borderWidth: 1.5,
            pointBackgroundColor: "#ffffff",
            pointBorderWidth: 1.5,
            pointRadius: 4,
            pointHoverBackgroundColor: "#127BBC",
            pointHoverBorderColor: "#ffffff",
            pointHoverRadius: 7,
            label: "Revenue ₹",
            data: reportData.leadSourceReportData.map(
              (item) => item.total_revenue
            ),
          },
        ],
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return "₹" + moneyFormatIndia(value);
              },
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: true,
            text: "Lead Source Revenue",
          },
        },
      },
    });
  };

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);

  // --- Date Range Picker Handler ---
  const handleDateCallback = (event, picker) => {
    const start = picker.startDate;
    const end = picker.endDate;

    const dateValue = `${start.format("DD-MM-YYYY")} - ${end.format("DD-MM-YYYY")}`;

    setFormInputs((prev) => ({
      ...prev,
      created_on: dateValue,
      screated_on: start.toDate(),
      ecreated_on: end.toDate(),
    }));
  };

  // Apply filters
  const applyFilters = (e) => {
    if (e) e.preventDefault();
    setFilters({
      created_on: formInputs.created_on,
      screated_on: formInputs.screated_on
        ? moment(formInputs.screated_on).format("DD-MM-YYYY")
        : "",
      ecreated_on: formInputs.ecreated_on
        ? moment(formInputs.ecreated_on).format("DD-MM-YYYY")
        : "",
      selecteduser: formInputs.selecteduser,
    });
  };

  // Helper functions
  const moneyFormatIndia = (amount, isCount = false) => {
    if (!amount && amount !== 0) return isCount ? "0" : "0.00";

    if (isCount) {
      return new Intl.NumberFormat("en-IN").format(parseFloat(amount));
    }

    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  // Calculate table totals
  const calculateTableTotals = () => {
    const totals = reportData.leadSourceReportData.reduce(
      (acc, item) => {
        acc.totalLeads += parseInt(item.total_leads) || 0;
        acc.totalSubscribedLeads += parseInt(item.total_subscribed_leads) || 0;
        acc.totalRevenue += parseFloat(item.total_revenue) || 0;
        acc.totalCost += parseFloat(item.total_cost) || 0;
        return acc;
      },
      {
        totalLeads: 0,
        totalSubscribedLeads: 0,
        totalRevenue: 0,
        totalCost: 0,
      }
    );

    return totals;
  };

  const totals = calculateTableTotals();
  const conversionRate =
    totals.totalLeads > 0
      ? (totals.totalSubscribedLeads / totals.totalLeads) * 100
      : 0;
  const avgOrderValue =
    totals.totalSubscribedLeads > 0
      ? totals.totalRevenue / totals.totalSubscribedLeads
      : totals.totalRevenue;
  const costPerLead =
    totals.totalLeads > 0 ? totals.totalCost / totals.totalLeads : 0;
  const roas =
    totals.totalCost > 0
      ? totals.totalRevenue / totals.totalCost
      : totals.totalRevenue;

  // Skeleton loader for summary boxes
  const SummarySkeleton = () => (
    <div className="row">
      {Array.from({ length: 5 }).map((_, index) => (
        <div className="col-sm-4" key={index}>
          <div className="widget widget-stats skeleton-card">
            <div className="widget-stats-left">
              <div className="skeleton-text" style={{ width: "70%", height: "16px", marginBottom: "10px" }}></div>
            </div>
            <div className="widget-stats-right">
              <div className="skeleton-text" style={{ width: "50%", height: "28px", marginBottom: "5px" }}></div>
              <div className="skeleton-text" style={{ width: "80%", height: "14px" }}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Skeleton loader for chart
  const ChartSkeleton = () => (
    <div className="row">
      <div className="col-12 mt-5">
        <div className="row">
          <div className="col-sm-6 offset-sm-3">
            <div className="skeleton-chart-container" style={{ height: "400px", position: "relative" }}>
              <div className="skeleton-chart" style={{ height: "100%", width: "100%" }}>
                <div className="skeleton-bar-chart"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Skeleton loader for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: 8 }).map((_, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`} className="skeleton-row">
          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
        </tr>
      ))}
      {/* Table footer skeleton */}
      <tr className="skeleton-row">
        <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "40%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "40%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
        <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
      </tr>
    </>
  );

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* Injecting the same CSS Link as SubscriptionAnalysisChart */}
      <link href="https://research.streetgains.in/dev/app/assets/daterangepicker.css" rel="stylesheet" />

      {/* Breadcrumb */}
      <div className="row pb-2">
        <div className="col-md-3">
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" style={{ textDecoration: "none" }}>
                <FontAwesomeIcon icon={faHome} /> Home
              </a>
            </li>
            <li className="breadcrumb-item active">Lead Source Report</li>
          </ul>
        </div>
      </div>

      {/* Main Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow chartbox">
        <div className="panel-heading"></div>
        <div className="panel-body">
          {/* Filter Form */}
          <div className="row">
            <div className="col-12">
              <form name="sesform" id="sesform" onSubmit={applyFilters}>
                <div className="row align-items-center">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label className="control-label">Created Date</label>

                      {/* Using React Bootstrap DateRangePicker - Same as SubscriptionAnalysisChart */}
                      <DateRangePicker
                        initialSettings={{
                          autoUpdateInput: false,
                          locale: {
                            format: "DD-MM-YYYY",
                            cancelLabel: "Clear",
                          },
                          ranges: ranges,
                          // Use State values for Start/End to ensure picker matches input
                          startDate: formInputs.screated_on
                            ? moment(formInputs.screated_on)
                            : moment(),
                          endDate: formInputs.ecreated_on
                            ? moment(formInputs.ecreated_on)
                            : moment(),
                        }}
                        onApply={handleDateCallback}
                      >
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            value={formInputs.created_on}
                            placeholder="Select Date Range"
                            readOnly
                          />
                          <span
                            className="input-group-addon"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "0 10px",
                              background: "#eee",
                              border: "1px solid #ced4da",
                              borderLeft: "none",
                            }}
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                          </span>
                        </div>
                      </DateRangePicker>
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <label className="control-label w-100"></label>
                      <button type="submit" className="btn btn-default">
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Summary Boxes */}
          {loadingStates.summary ? (
            <SummarySkeleton />
          ) : (
            <div className="row">
              <div className="col-sm-4">
                <a
                  href="#"
                  className="widget widget-stats bg-gradient-blue-dark inverse-mode"
                >
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Revenue</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      ₹{moneyFormatIndia(summaryBoxes.totalRevenue.current)}
                    </div>
                    {summaryBoxes.totalRevenue.html}
                  </div>
                </a>
              </div>

              <div className="col-sm-4">
                <a
                  href="#"
                  className="widget widget-stats bg-gradient-orange inverse-mode"
                >
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Spent</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      ₹{moneyFormatIndia(summaryBoxes.totalCost.current)}
                    </div>
                    {summaryBoxes.totalCost.html}
                  </div>
                </a>
              </div>

              <div className="col-sm-4">
                <a
                  href="#"
                  className="widget widget-stats bg-gradient-red inverse-mode"
                >
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">
                      Leads Conversion Ratio
                    </div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {summaryBoxes.totalLeadConRatio.current.toFixed(2)}%
                    </div>
                    {summaryBoxes.totalLeadConRatio.html}
                  </div>
                </a>
              </div>

              <div className="col-sm-4">
                <a
                  href="#"
                  className="widget widget-stats bg-gradient-green inverse-mode"
                >
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Leads</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {moneyFormatIndia(summaryBoxes.totalLeads.current, true)}
                    </div>
                    {summaryBoxes.totalLeads.html}
                  </div>
                </a>
              </div>

              <div className="col-sm-4">
                <a
                  href="#"
                  className="widget widget-stats bg-gradient-purple inverse-mode"
                >
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">
                      Total Subscribed Leads
                    </div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {moneyFormatIndia(
                        summaryBoxes.totalSubscribedLeads.current,
                        true
                      )}
                    </div>
                    {summaryBoxes.totalSubscribedLeads.html}
                  </div>
                </a>
              </div>
            </div>
          )}

          {/* Chart Section */}
          {loadingStates.chart ? (
            <ChartSkeleton />
          ) : (
            <div className="row">
              <div className="col-12 mt-5">
                <div className="row">
                  <div className="col-sm-6 offset-sm-3">
                    <canvas
                      ref={chartRef}
                      id="line_chart_lead_source_revenue"
                      title="Lead Source Revenue"
                    ></canvas>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="col-md-12 mt-5">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>Lead Source</th>
                    <th style={{ textAlign: "center" }}>Number of Leads</th>
                    <th style={{ textAlign: "center" }}>Converted</th>
                    <th style={{ textAlign: "center" }}>% Conversion</th>
                    <th style={{ textAlign: "center" }}>Avg Order Value</th>
                    <th style={{ textAlign: "center" }}>Cost Per Lead</th>
                    <th style={{ textAlign: "center" }}>Revenue</th>
                    <th style={{ textAlign: "center" }}>Total Cost</th>
                    <th style={{ textAlign: "center" }}>
                      Return On Ads Spent(ROAS)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingStates.table ? (
                    <TableSkeleton />
                  ) : reportData.leadSourceReportData.length > 0 ? (
                    <>
                      {reportData.leadSourceReportData.map((item, index) => (
                        <tr key={index}>
                          <td>{item.lead_source}</td>
                          <td style={{ textAlign: "center" }}>
                            {moneyFormatIndia(item.total_leads, true)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {moneyFormatIndia(
                              item.total_subscribed_leads,
                              true
                            )}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {item.total_leads > 0
                              ? (
                                (item.total_subscribed_leads /
                                  item.total_leads) *
                                100
                              ).toFixed(2)
                              : "0.00"}
                            %
                          </td>
                          <td style={{ textAlign: "center" }}>
                            ₹
                            {item.total_subscribed_leads > 0
                              ? moneyFormatIndia(
                                item.total_revenue /
                                item.total_subscribed_leads
                              )
                              : moneyFormatIndia(item.total_revenue)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            ₹
                            {item.total_leads > 0
                              ? moneyFormatIndia(
                                item.total_cost / item.total_leads
                              )
                              : moneyFormatIndia(item.total_cost)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            ₹{moneyFormatIndia(item.total_revenue)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            ₹{moneyFormatIndia(item.total_cost)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {item.total_cost > 0
                              ? moneyFormatIndia(
                                item.total_revenue / item.total_cost
                              )
                              : moneyFormatIndia(item.total_revenue)}
                          </td>
                        </tr>
                      ))}
                      {totals.totalLeads > 0 && (
                        <tfoot>
                          <tr>
                            <th style={{ textAlign: "center" }}>Total</th>
                            <th style={{ textAlign: "center" }}>
                              {moneyFormatIndia(totals.totalLeads, true)}
                            </th>
                            <th style={{ textAlign: "center" }}>
                              {moneyFormatIndia(totals.totalSubscribedLeads, true)}
                            </th>
                            <th style={{ textAlign: "center" }}>
                              {conversionRate.toFixed(2)}%
                            </th>
                            <th style={{ textAlign: "center" }}>
                              ₹{moneyFormatIndia(avgOrderValue)}
                            </th>
                            <th style={{ textAlign: "center" }}>
                              ₹{moneyFormatIndia(costPerLead)}
                            </th>
                            <th style={{ textAlign: "center" }}>
                              ₹{moneyFormatIndia(totals.totalRevenue)}
                            </th>
                            <th style={{ textAlign: "center" }}>
                              ₹{moneyFormatIndia(totals.totalCost)}
                            </th>
                            <th style={{ textAlign: "center" }}>
                              ₹{moneyFormatIndia(roas)}
                            </th>
                          </tr>
                        </tfoot>
                      )}
                    </>
                  ) : (
                    <tr style={{ textAlign: "center" }}>
                      <td colSpan="9">No Data Found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Skeleton Loading Styles */
        .skeleton-text {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px;
          display: inline-block;
        }

        .skeleton-card {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          ) !important;
          background-size: 200% 100% !important;
          animation: skeleton-loading 1.5s ease-in-out infinite !important;
          min-height: 80px;
          border: none !important;
        }

        .skeleton-chart-container {
          background: #f8f9fa;
          border-radius: 4px;
          overflow: hidden;
          border: 1px solid #eee;
        }

        .skeleton-chart {
          position: relative;
          overflow: hidden;
        }

        .skeleton-bar-chart {
          display: flex;
          align-items: flex-end;
          justify-content: space-around;
          padding: 40px 20px 20px 20px;
          height: 100%;
        }

        .skeleton-bar-chart::before,
        .skeleton-bar-chart::after {
          content: '';
          width: 15%;
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px 4px 0 0;
          margin: 0 10px;
        }

        .skeleton-bar-chart::before {
          height: 70%;
        }

        .skeleton-bar-chart::after {
          height: 50%;
        }

        .skeleton-row td {
          padding: 15px !important;
          vertical-align: middle !important;
        }

        .skeleton-row .skeleton-text {
          margin: 4px 0;
        }

        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        /* Existing Styles */
        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
          list-style: none;
          display: flex;
          align-items: center;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
          padding: 0 8px;
          color: #6c757d;
        }

        .breadcrumb-item a {
          color: #007bff;
          text-decoration: none;
        }

        .breadcrumb-item.active {
          color: #6c757d;
        }

        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 20px;
        }

        .panel-heading {
          padding: 10px 15px;
          border-bottom: 1px solid transparent;
          border-top-left-radius: 3px;
          border-top-right-radius: 3px;
        }

        .panel-body {
          padding: 15px;
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .widget-stats {
          padding: 15px;
          border-radius: 4px;
          color: white;
          min-height: 80px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 15px;
          text-decoration: none;
        }

        .widget-stats:hover {
          text-decoration: none;
          color: white;
          opacity: 0.9;
        }

        .bg-gradient-blue-dark {
          background: linear-gradient(45deg, #2c3e50, #3498db);
        }

        .bg-gradient-orange {
          background: linear-gradient(45deg, #e67e22, #e74c3c);
        }

        .bg-gradient-red {
          background: linear-gradient(45deg, #c0392b, #e74c3c);
        }

        .bg-gradient-green {
          background: linear-gradient(45deg, #27ae60, #2ecc71);
        }

        .bg-gradient-purple {
          background: linear-gradient(45deg, #8e44ad, #9b59b6);
        }

        .widget-stats-title {
          font-size: 14px;
          opacity: 0.9;
          margin-bottom: 5px;
        }

        .widget-stats-value {
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 5px;
        }

        .badge {
          font-size: 11px;
          padding: 3px 6px;
          border-radius: 3px;
        }

        .badge-success {
          background-color: #28a745;
        }

        .badge-danger {
          background-color: #dc3545;
        }

        .font-weight-normal {
          font-weight: 400;
        }

        .table {
          width: 100%;
          margin-bottom: 1rem;
          background-color: transparent;
        }

        .table th,
        .table td {
          padding: 0.75rem;
          vertical-align: top;
          border-top: 1px solid #dee2e6;
        }

        .table thead th {
          vertical-align: bottom;
          border-bottom: 2px solid #dee2e6;
        }

        .table tbody + tbody {
          border-top: 2px solid #dee2e6;
        }

        .btn-default {
          color: #333;
          background-color: #fff;
          border-color: #ccc;
        }

        .btn-default:hover {
          color: #333;
          background-color: #e6e6e6;
          border-color: #adadad;
        }

        .form-control {
          display: block;
          width: 100%;
          height: calc(2.25rem + 2px);
          padding: 0.375rem 0.75rem;
          font-size: 1rem;
          line-height: 1.5;
          color: #495057;
          background-color: #fff;
          background-clip: padding-box;
          border: 1px solid #ced4da;
          border-radius: 0.25rem;
          transition: border-color 0.15s ease-in-out,
            box-shadow 0.15s ease-in-out;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .control-label {
          display: inline-block;
          margin-bottom: 0.5rem;
        }

        .pull-right {
          float: right;
        }

        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
          line-height: 1.5;
          border-radius: 0.2rem;
        }

        /* Ensure the input group aligns correctly with the button */
        .input-group {
          display: flex;
          width: 100%;
        }
        .input-group .form-control {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          cursor: pointer;
          background: #fff !important;
        }
        .input-group-addon {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default LeadSourceReport;